import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Check, X, Star, ExternalLink, ArrowRight } from "lucide-react";
import { PLATFORMS, REGISTER_URL } from "@/data/site";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

/* Comparison matrix — every feature row gets a label, a short description
   (visible on hover for desktop, expanded on mobile), and a value per
   platform. Values can be: true, false, or a short string. */
const FEATURES = [
  { key: "execution",   label: "Execution model",        c: "ECN / DMA",       d: "Multi-asset retail", m: "Matched principal",   g: "Cloud-routed" },
  { key: "instruments", label: "Instruments",            c: 350,               d: 230,                  m: 180,                   g: 280 },
  { key: "dom",         label: "Depth of market (DOM)",  c: true,              d: false,                m: false,                 g: true },
  { key: "algos",       label: "Algos / bots",           c: "cBots & cAlgo",   d: false,                m: false,                 g: "Cloud APIs" },
  { key: "fix",         label: "FIX API",                c: true,              d: false,                m: false,                 g: true },
  { key: "web",         label: "Web platform",           c: true,              d: true,                 m: true,                  g: true },
  { key: "mobile",      label: "iOS + Android apps",     c: true,              d: true,                 m: true,                  g: true },
  { key: "desktop",     label: "Desktop apps",           c: true,              d: false,                m: false,                 g: false },
  { key: "copy",        label: "Native copy-trading",    c: true,              d: false,                m: true,                  g: false },
  { key: "minTrade",    label: "Min trade size",         c: "0.01 lot",        d: "0.01 lot",           m: "0.01 lot",            g: "0.001 lot" },
  { key: "exec_speed",  label: "Avg execution latency",  c: "<8 ms",           d: "~22 ms",             m: "~14 ms",              g: "<5 ms" },
  { key: "news",        label: "Built-in news",          c: true,              d: true,                 m: true,                  g: false },
  { key: "calendar",    label: "Economic calendar",      c: true,              d: true,                 m: true,                  g: false },
  { key: "tv_charts",   label: "TradingView charts",     c: true,              d: true,                 m: false,                 g: true },
  { key: "risk_tools",  label: "Risk dashboard",         c: true,              d: false,                m: true,                  g: true },
];

const KEY_MAP = { ctrader: "c", dxtrade: "d", matchtrader: "m", gooeypro: "g" };

const HIGHLIGHTS = {
  ctrader:     { tagline: "Best for ECN purists",         emphasis: "Native DOM + cBots", rating: 4.9 },
  dxtrade:     { tagline: "Best for casual mobile-first", emphasis: "No install, anywhere", rating: 4.6 },
  matchtrader: { tagline: "Best for copy-traders",        emphasis: "Mobile-first matching", rating: 4.7 },
  gooeypro:    { tagline: "Best for systematic algos",    emphasis: "Cloud-native, low latency", rating: 4.8 },
};

function Cell({ value }) {
  if (value === true) return <Check size={18} className="text-brand mx-auto" />;
  if (value === false) return <X size={18} className="text-[color:var(--text-3)]/50 mx-auto" />;
  return <span className="text-sm text-[color:var(--text-1)] tabular-nums">{value}</span>;
}

export default function ComparePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [highlighted, setHighlighted] = useState("ctrader");
  const cardRefs = useRef({});
  const tableRef = useRef(null);

  // Deep-link: /platforms/compare?focus=ctrader (or dxtrade / matchtrader / gooeypro)
  // → highlight that column and smooth-scroll its card into view.
  useEffect(() => {
    const focus = searchParams.get("focus");
    if (focus && KEY_MAP[focus]) {
      setHighlighted(focus);
      // wait for paint, then scroll the focused card into view
      const t1 = setTimeout(() => {
        const el = cardRefs.current[focus];
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 250);
      return () => clearTimeout(t1);
    }
  }, [searchParams]);

  // When the user actively picks a platform, scroll the comparison
  // table into view so they actually SEE what changed. Skipped on the
  // initial render (would jump on page load).
  const pickPlatform = (id) => {
    setHighlighted(id);
    requestAnimationFrame(() => {
      tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg)]" data-testid="compare-page">
      <Navbar />

      <main className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="pill-mono inline-flex mb-4">{t("compare.eyebrow", "Compare")}</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-4">
              {t("compare.title1", "Pick the platform that")} <span className="text-gradient-brand font-semibold">{t("compare.titleAccent", "fits your style")}</span>
            </h1>
            <p className="text-[color:var(--text-2)] max-w-2xl mx-auto text-sm lg:text-base font-light">
              {t("compare.lede", "Identical funding rules. Identical payouts. Four institutional platforms — choose the venue best suited to your strategy.")}
            </p>
          </div>

          {/* Platform headline cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-10">
            {PLATFORMS.map((p) => {
              const h = HIGHLIGHTS[p.id] || HIGHLIGHTS.ctrader;
              const isOn = highlighted === p.id;
              return (
                <motion.button
                  key={p.id}
                  ref={(el) => { cardRefs.current[p.id] = el; }}
                  type="button"
                  onClick={() => pickPlatform(p.id)}
                  data-testid={`compare-card-${p.id}`}
                  whileHover={{ y: -4 }}
                  className={`card-beam p-4 lg:p-5 text-left rounded-2xl transition-all ${isOn ? "race-border" : ""}`}
                  style={{
                    border: isOn
                      ? "1.5px solid color-mix(in srgb, var(--brand) 70%, transparent)"
                      : "1px solid color-mix(in srgb, var(--brand) 18%, transparent)",
                    boxShadow: isOn
                      ? "0 0 0 4px color-mix(in srgb, var(--brand) 14%, transparent)"
                      : "none",
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-lg bg-black/70 border border-[color:var(--line)] grid place-items-center overflow-hidden p-1.5">
                      <img src={p.logo} alt="" aria-hidden="true" className="w-full h-full object-contain" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-base font-medium text-[color:var(--text-1)] truncate">{p.name}</div>
                      <div className="flex items-center gap-1 text-[12px] text-brand">
                        <Star size={10} className="fill-brand" /> {h.rating}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-[color:var(--text-2)] mb-1 truncate">{h.tagline}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-3)] font-semibold">
                    {h.emphasis}
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* The big comparison table — desktop only */}
          <div
            ref={tableRef}
            className="card-beam overflow-hidden rounded-2xl hidden lg:block"
            data-testid="compare-table"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ background: "color-mix(in srgb, var(--brand) 5%, transparent)" }}>
                    <th className="text-left p-4 text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)] font-semibold w-[34%]">
                      Feature
                    </th>
                    {PLATFORMS.map((p) => (
                      <th
                        key={p.id}
                        className="p-4 text-center"
                        style={{
                          background: highlighted === p.id
                            ? "color-mix(in srgb, var(--brand) 10%, transparent)"
                            : "transparent",
                        }}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <img src={p.logo} alt="" aria-hidden="true" className="w-8 h-8 object-contain" />
                          <span className="text-xs font-medium text-[color:var(--text-1)]">{p.name}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {FEATURES.map((f, idx) => (
                    <tr key={f.key} className={idx % 2 ? "bg-[color:var(--surface-2)]/30" : ""}>
                      <td className="p-3.5 text-sm text-[color:var(--text-1)] font-medium">{f.label}</td>
                      {PLATFORMS.map((p) => {
                        const code = KEY_MAP[p.id] || "c";
                        return (
                          <td
                            key={p.id}
                            className="p-3.5 text-center"
                            style={{
                              background: highlighted === p.id
                                ? "color-mix(in srgb, var(--brand) 5%, transparent)"
                                : "transparent",
                            }}
                          >
                            <Cell value={f[code]} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile feature list — shows the SELECTED platform's value
              prominently per feature, with the other 3 platforms in a
              compact second row so users can still compare across
              platforms without horizontal scrolling. */}
          <div
            ref={tableRef}
            className="lg:hidden space-y-2"
            data-testid="compare-mobile"
          >
            <div className="card-beam rounded-2xl p-4 mb-3 flex items-center gap-3"
                 style={{
                   background: "color-mix(in srgb, var(--brand) 6%, var(--surface-2))",
                   border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)",
                 }}
            >
              <img
                src={PLATFORMS.find((p) => p.id === highlighted)?.logo}
                alt=""
                className="w-9 h-9 object-contain shrink-0"
              />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand font-semibold leading-none">Showing</div>
                <div className="font-display text-base font-medium text-[color:var(--text-1)] truncate mt-0.5">
                  {PLATFORMS.find((p) => p.id === highlighted)?.name}
                </div>
              </div>
              <div className="ml-auto text-[10px] text-[color:var(--text-3)] uppercase tracking-widest text-right shrink-0">
                Tap any<br />card above
              </div>
            </div>

            {FEATURES.map((f) => {
              const sel = KEY_MAP[highlighted] || "c";
              return (
                <div key={f.key} className="card-beam p-3.5 rounded-xl" data-testid={`compare-mobile-row-${f.key}`}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="text-[13px] font-medium text-[color:var(--text-1)] truncate">{f.label}</span>
                    <span className="shrink-0 inline-flex items-center justify-end min-w-[80px]">
                      <Cell value={f[sel]} />
                    </span>
                  </div>
                  {/* Other 3 platforms — tiny pills so users can still compare */}
                  <div className="flex flex-wrap gap-1.5">
                    {PLATFORMS.filter((p) => p.id !== highlighted).map((p) => {
                      const code = KEY_MAP[p.id] || "c";
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => pickPlatform(p.id)}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[12px] font-medium hover:bg-[color:var(--surface-3)] transition-colors"
                          style={{ background: "var(--surface-2)", border: "1px solid var(--line)" }}
                        >
                          <span className="text-[color:var(--text-3)] truncate max-w-[60px]">{p.name}</span>
                          <span className="text-[color:var(--text-1)] inline-flex items-center">
                            <Cell value={f[code]} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA strip */}
          <div className="mt-12 text-center">
            <p className="text-[color:var(--text-2)] text-sm mb-5">
              Still unsure? Start with a $50K challenge — you can switch platform on your next reset.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={REGISTER_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="compare-cta-register"
                className="btn-solid-brand text-sm inline-flex items-center justify-center gap-2"
                style={{ height: "48px", width: "220px", padding: "0 1.5rem", lineHeight: 1 }}
              >
                {t("compare.get_funded", "Get Funded")} <ArrowRight size={16} />
              </a>
              <a
                href="/challenges"
                data-testid="compare-cta-challenges"
                className="btn-ghost-gradient text-sm inline-flex items-center justify-center gap-2"
                style={{ height: "48px", width: "220px", padding: "0 1.5rem", lineHeight: 1 }}
              >
                View Challenges <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
